import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { PageShell } from '../components/PageShell';
import { useAuth } from '../store/authStore';
import { errorMessage } from '../lib/api';
import { toast } from '../store/uiStore';

type Tab = 'signin' | 'signup' | 'forgot' | 'magic';

export function AuthRoute() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      if (tab === 'signin') { await auth.login(email, password); toast('Signed in'); navigate('/'); }
      else if (tab === 'signup') { await auth.signup(email, password); setNotice('Check your email for an activation link. It expires in 24 hours.'); }
      else if (tab === 'forgot') { await auth.forgot(email); setNotice('If that address has an account, a reset link is on its way.'); }
      else { await auth.magic(email); setNotice('If that address has an account, a sign-in link is on its way (valid 15 minutes).'); }
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  if (auth.user) {
    return (
      <PageShell eyebrow="Account" title={auth.user.email}>
        <div className="glass panel stack" style={{ maxWidth: 420 }}>
          <p className="hint">Your settings and drones sync to this account.</p>
          <button type="button" className="btn" onClick={() => void auth.logout().then(() => { toast('Signed out'); navigate('/'); })}>Sign out</button>
        </div>
      </PageShell>
    );
  }

  const tabs: Array<[Tab, string]> = [['signin', 'Sign in'], ['signup', 'Sign up']];
  return (
    <PageShell eyebrow="Account" title={tab === 'signin' ? 'Sign in' : tab === 'signup' ? 'Create an account' : tab === 'forgot' ? 'Reset your password' : 'Email me a sign-in link'}>
      <form className="glass panel stack" style={{ maxWidth: 420 }} onSubmit={(e) => void submit(e)}>
        <div className="tabs" role="tablist">
          {tabs.map(([k, label]) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => { setTab(k); setError(null); setNotice(null); }}>{label}</button>)}
        </div>
        <div className="field">
          <label htmlFor="auth-email">Email</label>
          <input id="auth-email" className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        {(tab === 'signin' || tab === 'signup') && (
          <div className="field">
            <label htmlFor="auth-pw">Password{tab === 'signup' ? ' (10+ characters)' : ''}</label>
            <input id="auth-pw" className="input" type="password" autoComplete={tab === 'signup' ? 'new-password' : 'current-password'} required minLength={tab === 'signup' ? 10 : 1} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        )}
        {error && <div className="notice notice--err" role="alert">{error}</div>}
        {notice && <div className="notice notice--ok" role="status">{notice}</div>}
        <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? '…' : tab === 'signin' ? 'Sign in' : tab === 'signup' ? 'Sign up' : 'Send link'}</button>
        <div className="row hint">
          {tab !== 'forgot' && <button type="button" className="btn btn--sm btn--ghost" onClick={() => setTab('forgot')}>Forgot password</button>}
          {tab !== 'magic' && <button type="button" className="btn btn--sm btn--ghost" onClick={() => setTab('magic')}>Magic link</button>}
          {(tab === 'forgot' || tab === 'magic') && <button type="button" className="btn btn--sm btn--ghost" onClick={() => setTab('signin')}>Back to sign in</button>}
        </div>
        <p className="hint">You can play everything without an account. Signing in keeps your drones and settings across devices and lets you upload and publish.</p>
      </form>
    </PageShell>
  );
}
